import React from 'react';

const STATUS_STYLES = {
  completed: 'border-emerald-200 bg-emerald-50 text-emerald-800',
  success: 'border-emerald-200 bg-emerald-50 text-emerald-800',
  succeeded: 'border-emerald-200 bg-emerald-50 text-emerald-800',
  failed: 'border-red-200 bg-red-50 text-red-800',
  error: 'border-red-200 bg-red-50 text-red-800',
  cancelled: 'border-red-200 bg-red-50 text-red-800',
  running: 'border-blue-200 bg-blue-50 text-blue-800',
  queued: 'border-amber-200 bg-amber-50 text-amber-800',
  pending: 'border-amber-200 bg-amber-50 text-amber-800',
  needs_review: 'border-amber-200 bg-amber-50 text-amber-800',
  ready: 'border-emerald-200 bg-emerald-50 text-emerald-800',
  blocked: 'border-red-200 bg-red-50 text-red-800',
  enabled: 'border-emerald-200 bg-emerald-50 text-emerald-800',
  gated: 'border-amber-200 bg-amber-50 text-amber-800',
  manual: 'border-blue-200 bg-blue-50 text-blue-800',
};

export function statusClass(status) {
  return STATUS_STYLES[String(status || '').toLowerCase()] || 'border-gray-200 bg-gray-50 text-gray-700';
}

export function StatusPill({ children }) {
  return (
    <span className={`inline-flex rounded-full border px-2 py-0.5 text-xs font-medium ${statusClass(children)}`}>
      {String(children || 'unknown').replace(/_/g, ' ')}
    </span>
  );
}

export function CandidateSelect({ label, candidates, value, onChange, readyKey }) {
  const rows = candidates || [];
  if (!rows.length) return null;
  return (
    <label className="flex items-center gap-2 text-xs font-medium text-gray-600">
      <span>{label}</span>
      <select
        value={value}
        onChange={event => onChange(event.target.value)}
        className="rounded-lg border border-gray-300 bg-white px-2 py-2 text-sm font-medium text-gray-800 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
      >
        {rows.map(candidate => (
          <option key={candidate.key} value={candidate.key}>
            {candidate.key?.replace(/_/g, ' ')} · {candidate.provider} · {candidate.model} · {candidate[readyKey] ? 'ready' : 'blocked'}
          </option>
        ))}
      </select>
    </label>
  );
}

export function formatDate(value) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString();
}

export function formatDuration(value) {
  if (value === null || value === undefined || value === '') return '—';
  const seconds = Number(value);
  if (!Number.isFinite(seconds)) return '—';
  if (seconds < 60) return `${Math.round(seconds)}s`;
  return `${Math.round(seconds / 60)}m`;
}

export function percent(value) {
  const n = Number(value || 0);
  return `${Math.round(n * 100)}%`;
}

function downloadTextFile(filename, body, type = 'application/octet-stream') {
  const blob = new Blob([body || ''], { type });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export function downloadJobArtifact(artifact, fallbackName) {
  if (!artifact || typeof artifact.content !== 'string') return false;
  downloadTextFile(
    artifact.filename || fallbackName,
    artifact.content,
    artifact.content_type || 'application/octet-stream',
  );
  return true;
}

export function downloadCardEnrichmentCandidateArtifacts(artifacts = {}) {
  return [
    downloadJobArtifact(artifacts.tasks_jsonl, 'card_enrichment_eval_tasks.jsonl'),
    downloadJobArtifact(artifacts.candidate_outputs_jsonl, 'card_enrichment_candidate_outputs.jsonl'),
    downloadJobArtifact(artifacts.eval_report_csv, 'card_enrichment_eval_report.csv'),
    downloadJobArtifact(artifacts.eval_report_json, 'card_enrichment_eval_report.json'),
    downloadJobArtifact(artifacts.source_selection_manifest_json, 'card_enrichment_eval_source_manifest.json'),
  ].filter(Boolean).length;
}

export function MetricCard({ icon: Icon, label, value, tone = 'slate' }) {
  const toneClasses = {
    slate: 'border-slate-200 bg-white text-slate-800',
    green: 'border-emerald-200 bg-emerald-50 text-emerald-900',
    amber: 'border-amber-200 bg-amber-50 text-amber-900',
    red: 'border-red-200 bg-red-50 text-red-900',
    blue: 'border-blue-200 bg-blue-50 text-blue-900',
  };
  return (
    <div className={`rounded-lg border p-3 ${toneClasses[tone] || toneClasses.slate}`}>
      <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide opacity-70">
        {Icon && <Icon className="h-4 w-4" />}
        {label}
      </div>
      <div className="mt-2 text-2xl font-semibold">{value}</div>
    </div>
  );
}

export function CountList({ rows }) {
  const entries = Object.entries(rows || {}).sort((a, b) => b[1] - a[1]);
  if (!entries.length) return <p className="text-sm text-gray-500">No active counts.</p>;
  return (
    <div className="flex flex-wrap gap-2">
      {entries.map(([key, count]) => (
        <span key={key} className="rounded-full border border-gray-200 bg-white px-2 py-1 text-xs text-gray-700">
          {key.replace(/_/g, ' ')} · <span className="font-semibold">{count}</span>
        </span>
      ))}
    </div>
  );
}

export function sourceTotal(sourceCounts, sourceNames) {
  return sourceNames.reduce((total, source) => total + Number(sourceCounts?.[source] || 0), 0);
}

export function countRuns(items, predicate) {
  return (items || []).filter(predicate).length;
}

export function countStatuses(items, statuses) {
  const allowed = new Set(statuses);
  return countRuns(items, run => allowed.has(String(run.status || '').toLowerCase()));
}

export function statusCountsForRuns(items) {
  return (items || []).reduce((counts, run) => {
    const status = run.status || 'unknown';
    counts[status] = (counts[status] || 0) + 1;
    return counts;
  }, {});
}

export function qualityRowsFor(rows, sourceNames) {
  const allowed = new Set(sourceNames);
  return (rows || []).filter(row => allowed.has(row.source_type));
}

export function TabButton({ tab, active, onClick }) {
  const Icon = tab.icon;
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-2 border-b-2 px-3 py-2 text-sm font-medium ${
        active
          ? 'border-blue-600 text-blue-700'
          : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-800'
      }`}
    >
      {Icon && <Icon className="h-4 w-4" />}
      <span>{tab.label}</span>
      {tab.count !== undefined && (
        <span className={`rounded-full px-1.5 py-0.5 text-xs ${active ? 'bg-blue-50 text-blue-700' : 'bg-gray-100 text-gray-600'}`}>
          {tab.count}
        </span>
      )}
    </button>
  );
}

export function RunsTable({ items, retrying, onRetry }) {
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200 text-sm">
        <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
          <tr>
            <th className="px-4 py-2 text-left font-medium">Worker</th>
            <th className="px-4 py-2 text-left font-medium">Status</th>
            <th className="px-4 py-2 text-left font-medium">Place</th>
            <th className="px-4 py-2 text-left font-medium">Card</th>
            <th className="px-4 py-2 text-left font-medium">Started</th>
            <th className="px-4 py-2 text-left font-medium">Duration</th>
            <th className="px-4 py-2 text-left font-medium">Error</th>
            <th className="px-4 py-2 text-right font-medium">Action</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {(items || []).map(run => {
            const runKey = `${run.run_table}:${run.id}`;
            const canRetry = ['failed', 'error', 'cancelled'].includes(String(run.status || '').toLowerCase())
              && ['scrape_runs', 'enrichment_runs'].includes(run.run_table);
            return (
              <tr key={runKey} className="align-top">
                <td className="px-4 py-3">
                  <div className="font-medium text-gray-900">{run.worker || 'unknown'}</div>
                  <div className="text-xs text-gray-500">{run.run_type || run.run_table}</div>
                </td>
                <td className="px-4 py-3"><StatusPill>{run.status}</StatusPill></td>
                <td className="px-4 py-3 font-mono text-xs text-gray-700">{run.place_master_id || '—'}</td>
                <td className="px-4 py-3 font-mono text-xs text-gray-700">{run.card_key || '—'}</td>
                <td className="px-4 py-3 text-gray-600">{formatDate(run.started_at || run.created_at)}</td>
                <td className="px-4 py-3 text-gray-600">{formatDuration(run.duration_seconds)}</td>
                <td className="max-w-sm px-4 py-3 text-xs text-red-700">{run.error || '—'}</td>
                <td className="px-4 py-3 text-right">
                  {canRetry ? (
                    <button
                      type="button"
                      onClick={() => onRetry(run)}
                      disabled={retrying === runKey}
                      className="rounded-lg border border-gray-300 bg-white px-2.5 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                    >
                      {retrying === runKey ? 'Retrying...' : 'Retry'}
                    </button>
                  ) : (
                    <span className="text-xs text-gray-400">—</span>
                  )}
                </td>
              </tr>
            );
          })}
          {!(items || []).length && (
            <tr><td colSpan="8" className="px-4 py-8 text-center text-sm text-gray-500">No worker runs yet.</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

export function QualityTable({ rows, emptyText = 'No reviewed worker proposal fields yet.' }) {
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200 text-sm">
        <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
          <tr>
            <th className="px-3 py-2 text-left font-medium">Source</th>
            <th className="px-3 py-2 text-left font-medium">Template</th>
            <th className="px-3 py-2 text-right font-medium">Total</th>
            <th className="px-3 py-2 text-right font-medium">Heavy edit</th>
            <th className="px-3 py-2 text-right font-medium">Reject</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {(rows || []).map(row => (
            <tr key={`${row.source_type}:${row.source_label}:${row.template_version}`}>
              <td className="px-3 py-2">
                <div className="font-medium">{row.source_type}</div>
                <div className="text-xs text-gray-500">{row.source_label}</div>
              </td>
              <td className="px-3 py-2 font-mono text-xs">{row.template_version}</td>
              <td className="px-3 py-2 text-right">{row.total}</td>
              <td className="px-3 py-2 text-right">{percent(row.heavy_edit_rate)}</td>
              <td className="px-3 py-2 text-right">{percent(row.reject_rate)}</td>
            </tr>
          ))}
          {!(rows || []).length && (
            <tr><td colSpan="5" className="px-3 py-8 text-center text-sm text-gray-500">{emptyText}</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

export function Panel({ title, children, accessory }) {
  return (
    <section className="rounded-lg border border-gray-200 bg-white">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-200 px-4 py-3">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-700">{title}</h2>
        {accessory}
      </div>
      <div className="p-4">{children}</div>
    </section>
  );
}
